import { bookingErrorKey } from '@cleverbooking-fe-monorepo/booking-payment';

import type { ApiErrorBody } from './contracts';

/**
 * Typed marketplace API error + the ONLY way an API failure becomes UI text.
 *
 * `api()` throws `ApiError`. It is still `instanceof Error` and still carries
 * `.status`, so existing `(e as { status?: number }).status` checks keep
 * working. Its `.message` is a parsed, human-ish string meant for logs —
 * components must render `toUserMessage(err, t)` instead, which only ever
 * returns translated catalog strings (never a response body).
 */

/** Pseudo-status for "the request never got a response" (offline, DNS, CORS). */
export const NETWORK_ERROR_STATUS = 0;

/** Max length of `.message` when it falls back to a non-JSON body. */
const MAX_FALLBACK_MESSAGE = 200;

export interface ApiErrorInit {
  status: number;
  code?: string;
  message: string;
  details?: Record<string, unknown> | null;
  retryable?: boolean;
  /** Raw response text (or the thrown network error's message). Never render it. */
  raw: string;
  /** Request path, e.g. "/api/v1/marketplace/auth/consume-magic-link". */
  path?: string;
  /** Seconds from a `Retry-After` header, when present (429). */
  retryAfterSeconds?: number;
}

export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly status: number;
  readonly code?: string;
  readonly details?: Record<string, unknown> | null;
  readonly retryable?: boolean;
  readonly raw: string;
  readonly path?: string;
  readonly retryAfterSeconds?: number;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.status = init.status;
    this.code = init.code;
    this.details = init.details;
    this.retryable = init.retryable;
    this.raw = init.raw;
    this.path = init.path;
    this.retryAfterSeconds = init.retryAfterSeconds;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/**
 * Builds an `ApiError` from a non-2xx response body. Understands the shapes
 * the backend actually sends:
 *   - `{ code, message, details?, retryable? }` (BookingExceptionMapper)
 *   - `{ error }` (auth endpoints, e.g. consume-magic-link 401)
 *   - `{ message }`
 *   - anything else → plain text (truncated into `.message`, kept whole in `.raw`).
 */
export function parseErrorBody(
  status: number,
  statusText: string,
  text: string,
  extra: { path?: string; retryAfterSeconds?: number } = {},
): ApiError {
  const fallback = `${status} ${statusText}`.trim();
  let body: ApiErrorBody | null = null;
  const trimmed = text.trim();
  if (trimmed.startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        body = parsed as ApiErrorBody;
      }
    } catch {
      body = null;
    }
  }

  if (body) {
    const message =
      (typeof body.message === 'string' && body.message) ||
      (typeof body.error === 'string' && body.error) ||
      fallback;
    return new ApiError({
      status,
      code: typeof body.code === 'string' && body.code ? body.code : undefined,
      message,
      details:
        body.details && typeof body.details === 'object' ? body.details : undefined,
      retryable: typeof body.retryable === 'boolean' ? body.retryable : undefined,
      raw: text,
      ...extra,
    });
  }

  return new ApiError({
    status,
    message: trimmed ? trimmed.slice(0, MAX_FALLBACK_MESSAGE) : fallback,
    raw: text,
    ...extra,
  });
}

/** Reads a failed `Response` into an `ApiError`. Never throws. */
export async function errorFromResponse(res: Response, path?: string): Promise<ApiError> {
  const text = await res.text().catch(() => '');
  const retryAfter = Number(res.headers?.get?.('Retry-After'));
  return parseErrorBody(res.status, res.statusText ?? '', text, {
    path,
    retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
  });
}

/** Wraps a thrown `fetch` failure (offline, DNS, CORS) as an `ApiError`. */
export function networkError(cause: unknown, path?: string): ApiError {
  const raw = cause instanceof Error ? cause.message : String(cause ?? '');
  return new ApiError({
    status: NETWORK_ERROR_STATUS,
    code: 'network_error',
    message: 'Network request failed',
    raw,
    path,
  });
}

/**
 * True when the endpoint doesn't exist (yet) on the backend: 404/405/501
 * without a structured error code. Used by the redesign hooks to degrade to
 * `null`/empty instead of crashing while the backend catches up.
 */
export function isNotImplemented(err: unknown): boolean {
  const status = (err as { status?: unknown } | null)?.status;
  if (status !== 404 && status !== 405 && status !== 501) return false;
  return !(isApiError(err) && err.code);
}

/** Normalized (lower_snake) error code, or undefined. */
export function errorCode(err: unknown): string | undefined {
  const code = isApiError(err) ? err.code : (err as { code?: unknown } | null)?.code;
  return typeof code === 'string' && code ? code.toLowerCase() : undefined;
}

/**
 * 400 BOOKING_RULE_VIOLATION with `outside_business_hours` (backend cb77a9d):
 * a MOBILE booking outside the salon's home-base opening hours. The backend
 * puts the key in `message`; `details.reason` is accepted too. Takes an
 * `ApiError` (from `/book`) or a parsed payment-step body (`reason`, from
 * `/booking-intent`).
 */
export function isOutsideBusinessHours(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { code?: unknown; message?: unknown; reason?: unknown; details?: unknown };
  const code = typeof e.code === 'string' ? e.code.toLowerCase() : '';
  if (code === 'outside_business_hours') return true;
  if (code !== 'booking_rule_violation') return false;
  const details = e.details && typeof e.details === 'object' ? (e.details as Record<string, unknown>) : null;
  return [e.reason, e.message, details?.['reason']].some((v) => v === 'outside_business_hours');
}

/** 403 "Email verification required" (matched by text: the backend sends no code yet). */
export function isVerificationRequired(err: unknown): boolean {
  if (errorCode(err) === 'email_verification_required') return true;
  if (!isApiError(err) || err.status !== 403) return false;
  return /verification required/i.test(`${err.message} ${err.raw}`);
}

/** The i18n keys `toUserMessage` can return. All live under `errors.*`. */
export const ERROR_KEYS = {
  expiredLink: 'errors.expiredLink',
  slotTaken: 'errors.slotTaken',
  outsideBusinessHours: 'errors.outsideBusinessHours',
  holdExpired: 'errors.holdExpired',
  cancellationWindowPassed: 'errors.cancellationWindowPassed',
  cancellationWindowPassedHours: 'errors.cancellationWindowPassedHours',
  rescheduleWindowPassed: 'errors.rescheduleWindowPassed',
  rescheduleWindowPassedHours: 'errors.rescheduleWindowPassedHours',
  cancellationNotAllowed: 'errors.cancellationNotAllowed',
  cancelLimitReached: 'errors.cancelLimitReached',
  rescheduleNotAllowed: 'errors.rescheduleNotAllowed',
  bookingNotActive: 'errors.bookingNotActive',
  bookingNotFound: 'errors.bookingNotFound',
  invalidSlot: 'errors.invalidSlot',
  paymentRequired: 'errors.paymentRequired',
  verificationRequired: 'errors.verificationRequired',
  rateLimited: 'errors.rateLimited',
  rateLimitedMinutes: 'errors.rateLimitedMinutes',
  rateLimitedLaterToday: 'errors.rateLimitedLaterToday',
  rateLimitedTomorrow: 'errors.rateLimitedTomorrow',
  network: 'errors.network',
  signInRequired: 'errors.signInRequired',
  notFound: 'errors.notFound',
  alreadyReviewed: 'errors.alreadyReviewed',
  notEligibleToReview: 'errors.notEligibleToReview',
  notEligibleNotCompleted: 'errors.notEligibleNotCompleted',
  notEligibleCancelled: 'errors.notEligibleCancelled',
  notEligibleNoShow: 'errors.notEligibleNoShow',
  notEligibleExpired: 'errors.notEligibleExpired',
  notEligibleTooOld: 'errors.notEligibleTooOld',
  reviewTextTooLong: 'errors.reviewTextTooLong',
  reviewRatingRequired: 'errors.reviewRatingRequired',
  waitlistLimitReached: 'errors.waitlistLimitReached',
  waitlistInvalidRange: 'errors.waitlistInvalidRange',
  alertLimitReached: 'errors.alertLimitReached',
  confirmPendingFirst: 'errors.confirmPendingFirst',
  invalidRequest: 'errors.invalidRequest',
  generic: 'errors.generic',
} as const;

export type ErrorKey = (typeof ERROR_KEYS)[keyof typeof ERROR_KEYS];

/** English fallbacks so a missing translation never shows a bare key. */
const DEFAULTS: Record<ErrorKey, string> = {
  'errors.expiredLink': 'This sign-in link has expired or was already used. Request a new one.',
  'errors.slotTaken': 'Someone just booked that time. Please pick another slot.',
  'errors.outsideBusinessHours': "That time is outside the salon's opening hours — please pick another time",
  'errors.holdExpired': 'We held this time for 10 minutes and it has run out. Please pick a time again.',
  'errors.cancellationWindowPassed':
    'This booking can no longer be cancelled online. Please contact the salon.',
  'errors.cancellationWindowPassedHours':
    'Bookings can be cancelled online up to {{hours}} hours before the start. Please contact the salon.',
  'errors.rescheduleWindowPassed':
    'This booking can no longer be moved online. Please contact the salon.',
  'errors.rescheduleWindowPassedHours':
    'Bookings can be moved online up to {{hours}} hours before the start. Please contact the salon.',
  'errors.cancellationNotAllowed':
    "This salon doesn't allow online cancellation. Please contact the salon.",
  'errors.cancelLimitReached':
    "You've cancelled several prepaid bookings recently. Please contact the salon to cancel this one.",
  'errors.rescheduleNotAllowed':
    "This salon doesn't allow online changes. Please contact the salon.",
  'errors.bookingNotActive': "This booking is no longer active, so it can't be changed.",
  'errors.bookingNotFound': "We couldn't find that booking in your account.",
  'errors.invalidSlot': "That time can't be booked. Please pick another slot.",
  'errors.paymentRequired': 'This salon asks you to pay online to confirm this booking.',
  'errors.verificationRequired': 'Please confirm your email with the code we sent you.',
  'errors.rateLimited': 'Too many attempts. Please try again in a minute.',
  'errors.rateLimitedMinutes': 'Too many attempts. Please try again in {{count}} minutes.',
  'errors.rateLimitedLaterToday': 'Too many attempts. Please try again later today.',
  'errors.rateLimitedTomorrow': 'Too many attempts. Please try again tomorrow.',
  'errors.network': "We couldn't reach Salunas. Check your connection and try again.",
  'errors.signInRequired': 'Please sign in to continue.',
  'errors.notFound': "We couldn't find that. It may have been moved or removed.",
  'errors.alreadyReviewed': "You've already reviewed this booking.",
  'errors.notEligibleToReview': "This booking can't be reviewed.",
  'errors.notEligibleNotCompleted': 'You can review this booking once your visit is over.',
  'errors.notEligibleCancelled': "Cancelled bookings can't be reviewed.",
  'errors.notEligibleNoShow': "This booking was marked as missed, so it can't be reviewed.",
  'errors.notEligibleExpired': "This booking was never confirmed, so it can't be reviewed.",
  'errors.notEligibleTooOld': 'Reviews can be left up to 90 days after a visit.',
  'errors.reviewTextTooLong': 'Your review can be at most 1000 characters.',
  'errors.reviewRatingRequired': 'Choose a star rating from 1 to 5.',
  'errors.waitlistLimitReached':
    "You've reached the limit of time alerts at this salon. Wait for one to arrive, then try again.",
  'errors.waitlistInvalidRange': "These dates can't be watched. Pick a week within the salon's booking window.",
  'errors.alertLimitReached': "You've reached the limit of new-salon alerts.",
  'errors.confirmPendingFirst': 'Please confirm your earlier requests first (check your inbox).',
  'errors.invalidRequest': 'Please check what you entered and try again.',
  'errors.generic': 'Something went wrong. Please try again.',
};

const CODE_TO_KEY: Record<string, ErrorKey> = {
  slot_taken: ERROR_KEYS.slotTaken,
  time_slot_no_longer_available: ERROR_KEYS.slotTaken,
  hold_expired: ERROR_KEYS.holdExpired,
  cancellation_window_passed: ERROR_KEYS.cancellationWindowPassed,
  reschedule_window_passed: ERROR_KEYS.rescheduleWindowPassed,
  cancellation_not_allowed: ERROR_KEYS.cancellationNotAllowed,
  // Prepaid cancel cap (security hardening): 409 or 429 with `details.maxPerDay`.
  cancel_limit_reached: ERROR_KEYS.cancelLimitReached,
  reschedule_not_allowed: ERROR_KEYS.rescheduleNotAllowed,
  booking_not_active: ERROR_KEYS.bookingNotActive,
  booking_not_found: ERROR_KEYS.bookingNotFound,
  invalid_slot: ERROR_KEYS.invalidSlot,
  payment_required: ERROR_KEYS.paymentRequired,
  email_verification_required: ERROR_KEYS.verificationRequired,
  already_reviewed: ERROR_KEYS.alreadyReviewed,
  not_eligible: ERROR_KEYS.notEligibleToReview,
  waitlist_limit_reached: ERROR_KEYS.waitlistLimitReached,
  alert_limit_reached: ERROR_KEYS.alertLimitReached,
  invalid_request: ERROR_KEYS.invalidRequest,
  network_error: ERROR_KEYS.network,
};

function isConsumeMagicLink(err: ApiError): boolean {
  return !!err.path && err.path.includes('/auth/consume-magic-link');
}

/** Hours from `details` for the window-passed messages, if the server sent them. */
function windowHours(err: unknown): number | undefined {
  const details = isApiError(err) ? err.details : undefined;
  if (!details) return undefined;
  const candidate = details['hours'] ?? details['cancellationHours'] ?? details['rescheduleHours'];
  const n = Number(candidate);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * Seconds to wait before retrying a rate-limited call: the `Retry-After`
 * header, else `details.retryAfterSeconds` (the RATE_LIMITED body on /book,
 * /booking-intent, /auth/request-magic-link and /booking-verifications/start).
 */
function retryAfterSeconds(err: unknown): number | undefined {
  if (!isApiError(err)) return undefined;
  if (err.retryAfterSeconds) return err.retryAfterSeconds;
  const n = Number(err.details?.['retryAfterSeconds']);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/** Whole minutes until a 429's Retry-After, when it says more than ~1 minute. */
function retryMinutes(err: unknown): number | undefined {
  const s = retryAfterSeconds(err);
  return s && s > 90 ? Math.ceil(s / 60) : undefined;
}

/**
 * 429 copy from Retry-After (M6 QA): ≤ 90 s (or no header) "in a minute";
 * ≤ 60 min "in N minutes"; later the same local day "later today"; else
 * "tomorrow" (the review endpoint's daily cap lands here).
 */
function rateLimitKey(err: unknown, now: number): ErrorKey {
  const minutes = retryMinutes(err);
  if (!minutes) return ERROR_KEYS.rateLimited;
  if (minutes <= 60) return ERROR_KEYS.rateLimitedMinutes;
  const retryAt = new Date(now + minutes * 60_000);
  return retryAt.toDateString() === new Date(now).toDateString()
    ? ERROR_KEYS.rateLimitedLaterToday
    : ERROR_KEYS.rateLimitedTomorrow;
}

const NOT_ELIGIBLE_REASON_KEY: Record<string, ErrorKey> = {
  not_completed: ERROR_KEYS.notEligibleNotCompleted,
  cancelled: ERROR_KEYS.notEligibleCancelled,
  no_show: ERROR_KEYS.notEligibleNoShow,
  expired: ERROR_KEYS.notEligibleExpired,
  too_old: ERROR_KEYS.notEligibleTooOld,
};

/** `details[name]` as a string, if the server sent one. */
function detail(err: unknown, name: string): string | undefined {
  const details = isApiError(err) ? err.details : undefined;
  const v = details?.[name];
  return typeof v === 'string' && v ? v : undefined;
}

/** A 400 `invalid_request` the UI can say something specific about. */
function invalidRequestKey(err: ApiError): ErrorKey {
  const field = detail(err, 'field');
  const path = err.path ?? '';
  if (path.endsWith('/review')) {
    if (field === 'text') return ERROR_KEYS.reviewTextTooLong;
    if (field === 'rating') return ERROR_KEYS.reviewRatingRequired;
  }
  if (path.endsWith('/waitlist') && (field === 'from' || field === 'to')) return ERROR_KEYS.waitlistInvalidRange;
  return ERROR_KEYS.invalidRequest;
}

/**
 * Resolves which i18n key describes `err`. Returns a key under `errors.*`, or
 * — for an existing BookingErrorCode the shared lib already localizes — a
 * `booking.errors.*` key. Never returns anything derived from the body text
 * except the 403 verification sniff.
 */
export function userMessageKey(err: unknown, now: number = Date.now()): string {
  if (!err) return ERROR_KEYS.generic;

  // fetch() rejected with a non-ApiError (e.g. a TypeError from a caller that
  // bypassed api()) → treat as network.
  if (!isApiError(err)) {
    if (err instanceof TypeError) return ERROR_KEYS.network;
    const status = (err as { status?: unknown }).status;
    if (typeof status !== 'number') return ERROR_KEYS.generic;
  }

  const status = (err as { status: number }).status;
  if (status === NETWORK_ERROR_STATUS) return ERROR_KEYS.network;
  // Contract says 401; 400/404/410 are treated the same (an unusable token).
  if (isApiError(err) && [400, 401, 404, 410].includes(status) && isConsumeMagicLink(err)) {
    return ERROR_KEYS.expiredLink;
  }
  const code = errorCode(err);
  // The prepaid cancel cap may arrive as 429 too: it is a policy, not "slow down".
  if (code === 'cancel_limit_reached') return ERROR_KEYS.cancelLimitReached;
  if (status === 429 || code === 'rate_limited') return rateLimitKey(err, now);

  if (isOutsideBusinessHours(err)) return ERROR_KEYS.outsideBusinessHours;

  if (code) {
    const key = CODE_TO_KEY[code];
    if (key === ERROR_KEYS.cancellationWindowPassed && windowHours(err)) {
      return ERROR_KEYS.cancellationWindowPassedHours;
    }
    if (key === ERROR_KEYS.rescheduleWindowPassed && windowHours(err)) {
      return ERROR_KEYS.rescheduleWindowPassedHours;
    }
    if (key === ERROR_KEYS.notEligibleToReview) {
      return NOT_ELIGIBLE_REASON_KEY[detail(err, 'reason') ?? ''] ?? key;
    }
    if (key === ERROR_KEYS.invalidRequest && isApiError(err)) return invalidRequestKey(err);
    if (key) return key;
    const legacy = bookingErrorKey(code.toUpperCase());
    if (legacy && legacy !== 'booking.errors.generic') return legacy;
  }

  if (isVerificationRequired(err)) return ERROR_KEYS.verificationRequired;
  if (status === 402) return ERROR_KEYS.paymentRequired;
  if (status === 401) return ERROR_KEYS.signInRequired;
  if (status === 404) return ERROR_KEYS.notFound;
  return ERROR_KEYS.generic;
}

/**
 * i18next-compatible translate function. Written loosely so the app's `t`
 * from `useTranslation()` is assignable without casts.
 */
export type TranslateFn = (
  key: string,
  options?: { defaultValue?: string; hours?: number; count?: number },
) => unknown;

/**
 * The user-facing message for any thrown value. Always a translated catalog
 * string (with an English default) — a raw response body can never come out
 * of this function.
 */
export function toUserMessage(err: unknown, t: TranslateFn, now: number = Date.now()): string {
  const key = userMessageKey(err, now);
  const hours = windowHours(err);
  const defaultValue =
    (DEFAULTS as Record<string, string>)[key] ?? DEFAULTS[ERROR_KEYS.generic];
  // `count` picks the _one/_other plural form ("1 hour" / "24 hours").
  const minutes = key === ERROR_KEYS.rateLimitedMinutes ? retryMinutes(err) : undefined;
  const out = t(
    key,
    hours ? { defaultValue, hours, count: hours } : minutes ? { defaultValue, count: minutes } : { defaultValue },
  );
  return typeof out === 'string' && out ? out : defaultValue;
}

/**
 * Message for the waitlist and new-salon alert forms. Their 429 means the
 * email already has 3 unconfirmed double opt-in entries (backend 48fc1eb),
 * so it asks for those to be confirmed instead of "try again later".
 */
export function toSubscribeMessage(err: unknown, t: TranslateFn, now: number = Date.now()): string {
  const status = (err as { status?: unknown } | null)?.status;
  if (status === 429) {
    const key = ERROR_KEYS.confirmPendingFirst;
    const out = t(key, { defaultValue: DEFAULTS[key] });
    return typeof out === 'string' && out ? out : DEFAULTS[key];
  }
  return toUserMessage(err, t, now);
}
